import { Drill } from '@/components/drill-library/types/drill-types';
import { parseCSV, unescapeCSV } from './csv-utils';
import { transformValueForImport } from './drill-export-csv';
import { loadTaxonomy } from '@/lib/taxonomy-loader';

/**
 * Categories that support multiple values and are exported as checkbox-style columns
 * Even though they may be defined as 'select' type in the taxonomy
 */
const MULTI_VALUE_SELECT_CATEGORIES = ['Phase', 'Posture'];

export interface DrillImportData {
  drills: Drill[];
  duplicateCount: number;
  exportedAt?: string;
  version?: string;
}
import logger from '@/lib/logging';

/**
 * Extract YouTube video ID from URL
 */
function extractYouTubeId(url: string | undefined): string {
  if (!url || typeof url !== 'string') return '';
  
  try {
    const urlObj = new URL(url.trim());
    
    // Handle youtu.be short URLs
    if (urlObj.hostname === 'youtu.be') {
      const id = urlObj.pathname.slice(1);
      return id || '';
    }
    
    // Handle youtube.com URLs
    if (urlObj.hostname.includes('youtube.com')) {
      const id = urlObj.searchParams.get('v');
      return id || '';
    }
    
    // Handle /embed/{id} or /v/{id} URLs
    const parts = urlObj.pathname.split('/').filter(Boolean);
    const maybeId = parts[parts.length - 1];
    return maybeId || '';
  } catch {
    return '';
  }
}

/**
 * Parse CSV file and convert to DrillImportData
 */
export async function parseCSVFile(content: string): Promise<DrillImportData> {
  try {
    const rows = parseCSV(content);
    
    if (rows.length === 0) {
      throw new Error('CSV file is empty');
    }
    
    const headers = rows[0].map(h => h.trim());
    const dataRows = rows.slice(1);
    
    // Find fixed column indices (supporting both old and new formats)
    const youtubeIdIndex = headers.findIndex(h => h.toLowerCase() === 'youtube id');
    const titleIndex = headers.findIndex(h => h.toLowerCase() === 'title');
    const urlIndex = headers.findIndex(h => h.toLowerCase() === 'url');
    const descriptionIndex = headers.findIndex(h => h.toLowerCase() === 'description');
    
    // Find audit column indices
    const createdByIndex = headers.findIndex(h => h.toLowerCase() === 'created by');
    const createdAtIndex = headers.findIndex(h => h.toLowerCase() === 'created at');
    const changedByIndex = headers.findIndex(h => h.toLowerCase() === 'changed by');
    const changedAtIndex = headers.findIndex(h => h.toLowerCase() === 'changed at');
    
    if (titleIndex === -1 || urlIndex === -1 || descriptionIndex === -1) {
      throw new Error('CSV file is missing required columns: Title, URL, or Description');
    }
    
    // Parse taxonomy columns
    // Group checkbox columns by category prefix
    const selectColumns: Map<number, string> = new Map(); // column index -> category name
    const checkboxColumns: Map<number, { category: string; option: string }> = new Map(); // column index -> {category, option}
    
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i].trim();
      
      // Skip fixed columns and audit columns
      if (i === youtubeIdIndex || i === titleIndex || i === urlIndex || i === descriptionIndex ||
          i === createdByIndex || i === createdAtIndex || i === changedByIndex || i === changedAtIndex) {
        continue;
      }
      
      // Check if it's a checkbox column (format: "Category - Option")
      // Match from the right to handle category names that might contain " - "
      const lastDashIndex = header.lastIndexOf(' - ');
      if (lastDashIndex > 0) {
        const category = header.substring(0, lastDashIndex).trim();
        const option = header.substring(lastDashIndex + 3).trim();
        if (category && option) {
          checkboxColumns.set(i, { category, option });
          continue; // Skip adding to selectColumns
        }
      }
      
      // If not a checkbox column, assume it's a select category column
      selectColumns.set(i, header);
    }
    
    // Load taxonomy once for value transformation
    const taxonomyData = await loadTaxonomy();
    const taxonomy = taxonomyData.categories || {};
    
    // Convert rows to drills
    const drills: Drill[] = [];
    
    for (let rowIndex = 0; rowIndex < dataRows.length; rowIndex++) {
      const row = dataRows[rowIndex];
      
      // Skip empty rows
      if (row.every(cell => !cell || cell.trim() === '')) {
        continue;
      }
      
      const title = unescapeCSV(row[titleIndex] || '').trim();
      const url = unescapeCSV(row[urlIndex] || '').trim();
      const description = unescapeCSV(row[descriptionIndex] || '').trim();
      
      // Get YouTube ID from column or derive from URL
      let youtubeId = '';
      if (youtubeIdIndex !== -1) {
        youtubeId = unescapeCSV(row[youtubeIdIndex] || '').trim();
      }
      // If no YouTube ID column or it's empty, derive from URL
      if (!youtubeId) {
        youtubeId = extractYouTubeId(url);
      }
      
      // Validate required fields
      if (!title || !url) {
        logger.warn(`Row ${rowIndex + 2}: Skipping drill with missing title or URL`);
        continue;
      }
      
      // Build tags object
      const tags: { [key: string]: string | string[] } = {};
      
      // Process select columns (regular single-value selects only)
      for (const [colIndex, categoryName] of selectColumns) {
        // Skip multi-value select categories - they are handled as checkbox-style columns
        if (MULTI_VALUE_SELECT_CATEGORIES.includes(categoryName)) {
          continue;
        }
        
        const displayValue = unescapeCSV(row[colIndex] || '').trim();
        if (displayValue) {
          // CRITICAL: Handle legacy multi-value select tags (e.g., "Phase 2 | Phase 3")
          // Split on " | " separator to support older exports
          if (displayValue.includes(' | ')) {
            const values = displayValue.split(' | ').map(v => v.trim()).filter(v => v);
            const taxonomyValues = values.map(v => transformValueForImport(categoryName, v, taxonomy));
            tags[categoryName] = taxonomyValues.length === 1 ? taxonomyValues[0] : taxonomyValues;
          } else {
            // Single value - transform display value back to taxonomy value
            const taxonomyValue = transformValueForImport(categoryName, displayValue, taxonomy);
            tags[categoryName] = taxonomyValue;
          }
        }
      }
      
      // Process checkbox columns - group by category
      // This includes multi-value select categories (Phase, Pattern) which are exported as checkbox-style columns
      const checkboxValuesByCategory: { [category: string]: string[] } = {};
      for (const [colIndex, { category, option }] of checkboxColumns) {
        const value = unescapeCSV(row[colIndex] || '').trim().toUpperCase();
        // Accept 'X' as the standard checkbox value
        if (value === 'X') {
          if (!checkboxValuesByCategory[category]) {
            checkboxValuesByCategory[category] = [];
          }
          checkboxValuesByCategory[category].push(option);
        }
      }
      
      // Add checkbox values to tags
      // For multi-value select categories, store as array even if single value
      for (const [category, options] of Object.entries(checkboxValuesByCategory)) {
        if (options.length > 0) {
          // Multi-value select categories always store as array to maintain consistency
          if (MULTI_VALUE_SELECT_CATEGORIES.includes(category)) {
            tags[category] = options.length === 1 ? options[0] : options;
          } else {
            tags[category] = options;
          }
        }
      }
      
      // Determine mainCategory from tags (prefer Domain, then Category)
      let mainCategory = 'General';
      if (tags['Domain']) {
        mainCategory = Array.isArray(tags['Domain']) ? tags['Domain'][0] : tags['Domain'];
      } else if (tags['Category']) {
        mainCategory = Array.isArray(tags['Category']) ? tags['Category'][0] : tags['Category'];
      }
      
      // Get audit fields from CSV if available
      const createdBy = createdByIndex !== -1 ? unescapeCSV(row[createdByIndex] || '').trim() : 'imported';
      const createdAt = createdAtIndex !== -1 ? unescapeCSV(row[createdAtIndex] || '').trim() : new Date().toISOString();
      const changedBy = changedByIndex !== -1 ? unescapeCSV(row[changedByIndex] || '').trim() : undefined;
      const changedAt = changedAtIndex !== -1 ? unescapeCSV(row[changedAtIndex] || '').trim() : undefined;
      
      // Create drill object with YouTube ID as the primary ID
      const drill: Drill = {
        id: youtubeId || '', // Use YouTube ID as primary ID
        name: title,
        description: description,
        videoUrl: url,
        transcript: '', // Not in CSV
        tags: tags,
        mainCategory: mainCategory,
        createdBy: createdBy || 'imported',
        createdAt: createdAt || new Date().toISOString(),
        ...(changedBy && { changedBy }),
        ...(changedAt && { changedAt })
      };
      
      drills.push(drill);
    }
    
    return {
      drills: drills,
      duplicateCount: 0, // Will be calculated by caller
      exportedAt: undefined,
      version: undefined
    };
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to parse CSV file');
  }
}

